import {Feature, Geometry, LineString, Point, Polygon, Position} from "geojson";
import {MapMouseEvent} from "@mapbox/mapbox-gl-draw";
import {AbstractDraw, DrawInteractionState} from "./abstract-draw";
import {GeometryUtil} from "../geometry";
import {SnapPoint} from "../snap";
import {FeatureUtil} from "../feature";

export class AbstractDrawPolygon extends AbstractDraw<DrawInteractionState> {
  protected line: Feature<LineString> | undefined;
  protected firstPoint: Feature<Point> | undefined;
  protected lastPoint: Feature<Point> | undefined;
  protected polygon: Feature<Polygon> | undefined;

  buildInitialState(initialState: DrawInteractionState): DrawInteractionState {
    let state = super.buildInitialState(initialState);

    this._generateDrawingPolygon(state);
    this._generateDrawLine(state);
    this._generateFinalizationPoints(state);

    return state;
  }

  canSave(state: DrawInteractionState): boolean {
    return state.vertexList.length > 2;
  }

  async onMouseMove(state: DrawInteractionState, e: MapMouseEvent) {

    await super.onMouseMove(state, e);

    this.aim.geometry.coordinates = this.snapPoint.point as Position;

    this.displayDrawFeature(this.aim);

    if (this.line) {
      this._generateFinalizationPoints(state);

      if (state.vertexPosition > 1) {
        this._generateDrawingPolygon(state, this.snapPoint);
      }

      this._generateDrawLine(state, this.snapPoint);

    }
  }

  onStop(state: DrawInteractionState) {
    if (this.firstPoint) {
      this.removeDrawFeature(this.firstPoint.id);
      this.firstPoint = undefined;
    }
    if (this.lastPoint) {
      this.removeDrawFeature(this.lastPoint.id);
      this.lastPoint = undefined;
    }
    if (this.polygon) {
      this.removeDrawFeature(this.polygon.id);
      this.polygon = undefined;
    }
    super.onStop(state);
  }

  async onClick(state: DrawInteractionState, e: MapMouseEvent) {
    await super.onClick(state, e);

    if (e.defaultPrevented) {
      return;
    }

    this._generateFinalizationPoints(state);

    this._generateDrawingPolygon(state);

    this._generateDrawLine(state);
    this.tick();
  }

  async setCoordinates(state: DrawInteractionState, points: Position[]): Promise<void> {
    this.clearDrawFeatures();
    await super.setCoordinates(state, points);

    this._generateFinalizationPoints(state);

    this._generateDrawingPolygon(state);

    this._generateDrawLine(state);
    this.tick();
  }

  save(state: DrawInteractionState, nextInteraction: string | undefined = undefined) {
    this.createLayerGeometries(
      [GeometryUtil.polygon([...state.vertexList, state.vertexList[0]])],
      nextInteraction
    )
  }

  protected _generateFinalizationPoints(state: DrawInteractionState, lastPoint: SnapPoint | undefined = undefined): void {
    if (state.vertexPosition <= 2) {
      if (this.firstPoint) {
        this.removeDrawFeature(this.firstPoint.id.toString());
        this.firstPoint = undefined;
      }
      if (this.lastPoint) {
        this.removeDrawFeature(this.lastPoint.id.toString());
        this.lastPoint = undefined;
      }
      return;
    }
    this.lastPoint = this.createIcon(
      lastPoint?.point ?? state.vertexList[state.vertexPosition - 1],
      'map-check',
      {
        coord_path: (state.vertexPosition - 1).toString(),
        id: 'last_point',
        'icon-size': 0.5
      }
    );
    this.displayDrawFeature(this.lastPoint);

    this.firstPoint = this.createIcon(
      state.vertexList[0],
      'map-check',
      {
        coord_path: '0',
        id: 'first_point',
        'icon-size': 0.5
      }
    );

    this.displayDrawFeature(this.firstPoint);
  }

  protected _generateDrawingPolygon(state: DrawInteractionState, lastPoint: SnapPoint | undefined = undefined): void {
    if (state.vertexPosition < 2) {
      if (this.polygon) {
        this.removeDrawFeature(this.polygon.id.toString());
        this.polygon = undefined;
      }
      return;
    }
    let points = [...state.vertexList];
    if (lastPoint) {
      if (this.controller.snapUtility.isMode('trace') && state.snapFeature) {
        let trace = FeatureUtil.extractLineFrom(state.snapFeature.geometry as any, state.snapFeaturePath, lastPoint.path);
        points.push(...trace);
      }
      points.push(lastPoint.point);
    }

    this.polygon = this.createPolygon([...points, points[0]], 'tmp_polygon');
    this.polygon.properties._showArea = this.controller.interaction.hasArea();
    this.polygon.properties._showVertexLength = this.controller.interaction.hasVertexLength();
    this.polygon.properties._showLength = this.controller.interaction.hasLength();
    this.polygon.properties._showAngle = this.controller.interaction.hasAngle();
    this.displayDrawFeature(this.polygon);
  }

  protected _generateDrawLine(state: DrawInteractionState, lastPoint: SnapPoint | undefined = undefined): void {
    if (state.vertexList.length === 0 && this.line) {
      this.removeDrawFeature(this.line.id.toString());
      this.line = undefined;
      return;
    } else if (state.vertexList.length === 0) {
      return;
    }
    let vertexList = [...state.vertexList];

    if (lastPoint) {
      if (this.controller.snapUtility.isMode('trace') && state.snapFeature) {
        let trace = FeatureUtil.extractLineFrom(state.snapFeature.geometry as any, state.snapFeaturePath, lastPoint.path);
        vertexList.push(...trace);
      }
      vertexList.push(lastPoint.point);
    }
    this.line = this.createLineString(vertexList, 'tmp_line');
    if (!this.polygon) {
      this.line.properties._showVertexLength = this.controller.interaction.hasVertexLength();
    }
    this.displayDrawFeature(this.line);
  }
}
